import _isString from 'lodash-es/isString';
import _isObject from 'lodash-es/isObject';
import _forEach from 'lodash-es/forEach';
import _has from 'lodash-es/has';
import _trimStart from 'lodash-es/trimStart';
import _startsWith from 'lodash-es/startsWith';

import {
    Component, AfterViewChecked, DoCheck, Input,
    Output, EventEmitter, ContentChildren, QueryList, forwardRef, SimpleChanges
} from '@angular/core';
import { AutoComplete } from 'primeng/autocomplete';
import { DomHandler } from 'primeng/dom';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, FormGroup } from '@angular/forms';
import { ObjectUtils } from 'primeng/utils';
import { PrimeTemplate } from "primeng/api";

import { faTimesCircle, IconDefinition, faCaretDown, faPlane, faMapMarker } from "@fortawesome/free-solid-svg-icons";

export const AUTOCOMPLETE_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AutoCompleteFb),
    multi: true
};

@Component({

    selector: 'p-autoCompleteFb',
    templateUrl: './autoComplete-fb.component.html',
    host: {
        '[class.ui-inputwrapper-filled]': 'filled',
        '[class.ui-inputwrapper-focus]': 'focus'
    },
    providers: [DomHandler, ObjectUtils, AUTOCOMPLETE_VALUE_ACCESSOR]
})
export class AutoCompleteFb extends AutoComplete implements AfterViewChecked, ControlValueAccessor {

    @Input()
    public minLength: number = 1;

    @Input()
    public itemLabel: string;

    @Input()
    public delay: number = 300;

    @Input()
    public style: any;

    @Input()
    public styleClass: string;

    @Input()
    public inputStyle: any;

    @Input()
    public inputStyleClass: string;

    @Input()
    public placeholder: string;

    @Input()
    public readonly: boolean;

    @Input()
    public disabled: boolean;

    @Input()
    public maxlength: number;

    @Input()
    public size: number;

    // TODO NG UPGRADE @Input() suggestions

    @Input()
    public appendTo: any;

    @Output()
    public completeMethod: EventEmitter<any> = new EventEmitter();

    @Output()
    public onSelect: EventEmitter<any> = new EventEmitter();

    @Output()
    public onUnselect: EventEmitter<any> = new EventEmitter();

    @Output()
    public onFocus: EventEmitter<any> = new EventEmitter();

    @Output()
    public onBlur: EventEmitter<any> = new EventEmitter();

    @Output()
    public onDropdownClick: EventEmitter<any> = new EventEmitter();

    @Output()
    public keyUp: EventEmitter<any> = new EventEmitter();

    @Output()
    public onEmpty: EventEmitter<boolean> = new EventEmitter();

    @Input()
    public field: string;

    @Input()
    public scrollHeight: string = '200px';

    @Input()
    public dropdown: boolean;

    @Input()
    public multiple: boolean;

    @Input()
    public tabindex: number;

    @Input()
    public strict: boolean;

    @Input()
    public group: FormGroup;

    public iconClose: IconDefinition = faTimesCircle;
    public iconPlane: IconDefinition = faPlane;
    public iconCaretDown: IconDefinition = faCaretDown;
    public iconMapMarker: IconDefinition = faMapMarker;

    @ContentChildren(PrimeTemplate) public templates: QueryList<any>;

    public panelVisible: boolean = true;
    public objectUtils: ObjectUtils = new ObjectUtils();

    public isString(data: any): boolean {
        return _isString(data);
    }

    public isObject(data: any): boolean {
        return _isObject(data);
    }


    public onKeydown(event) {
        if (event.which === 9) {
            this.onInputBlur(event);
        } else {
            super.onKeydown(event);
            this.panelVisible = true;
        }
    }

    public onInputFocus(event) {
        super.onInputFocus(event);
    }


    public onInputBlur(event) {
        this.getValueFromSuggestions();

        this.focus = false;
        this.onModelTouched();
        this.onBlur.emit(event);
    }

    public ngOnChanges(simpleChanges: SimpleChanges) {
        if (!this.focus && _has(simpleChanges, 'suggestions.currentValue')) {
            this.getValueFromSuggestions();
        }
    }

    public getLabel(option) {
        let output: string = "";
        _forEach(this.itemLabel, (part: string) => {
            if (_startsWith(part, 'sep')) {
                output += _trimStart(part, 'sep');
            } else {
                if (option[part]) {
                    output += option[part];
                } else {
                    return undefined;
                }
            }
        });

        return output;
    }

    public getValueFromSuggestions(): void {
        const inputValue: string = this.inputEL?.nativeElement.value;
        if (this.highlightOption) {
            // If a value has been selected with keyboard up/down keys
            this.selectItemOnInput(this.highlightOption);
        } else if (_isString(inputValue)) {

            if (!this.suggestions) {
                return ;
            }
            // If input value exactly matches a suggestion code
            const upperInputValue: string = inputValue.toUpperCase();
            const selectedByTab: any = (this.suggestions || []).find(s => s.code === upperInputValue);
            const firstItem: any = this.suggestions[0];
            if (selectedByTab) {
                this.selectItemOnInput(selectedByTab);
            }else if(firstItem){
                this.selectItemOnInput(firstItem);
            }
        } else if (this.value === "" || !this.value) {
            // If empty
            this.onEmpty.emit(true);
        }

        this.panelVisible = false;
    }

    public selectItem(option: any) {
        this.selectItemOnInput(option);
        this.onSelect.emit(option);
        this.panelVisible = false;
        this.setFocus();
    }

    public selectItemOnInput(option: any) {
        if (this.multiple) {
            this.value = this.value || [];
            if (!this.isSelected(option)) {
                this.value.push(option);
                this.onModelChange(this.value);
            }
        } else {
            this.value = option;
            this.onModelChange(this.value);
        }
    }

    public getInputValue(_value) {
        if (this.itemLabel && _isObject(_value)) {
            return this.getLabel(_value);
        } else if (this.field) {
            return this.resolveFieldData(_value);
        } else {
            return "";
        }
    }

    public selecInputContent() {
        this.invokeOnInput('select', []);
    }

    public setFocus() {
        this.invokeOnInput('focus', []);
    }

    public objectUtilsResolveFieldData(value, field: string): any {
        return ObjectUtils.resolveFieldData(value, field);
    }

    public resolveFieldData(value) {
        // console.warn("TODO NG UPGRADE resolveFieldData(value)");
        return value;
    }

    private invokeOnInput(name: string, parameters: any[]) {
        if (this.multiple) {
            this.multiInputEL.nativeElement[name](...parameters);
        } else {
            this.inputEL.nativeElement[name](...parameters);
        }
    }
}
